import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, RefreshCw } from "lucide-react";
import { scScreener } from "../../lib/sifcontrolApi";

const STRATEGY = ["", "Equity Long-Short", "Equity Ex-Top 100", "Hybrid", "Debt", "Multi Asset", "Other"];

const NumField = ({ label, value, onChange, hint, testId }) => (
    <label className="block">
        <div className="text-[10px] uppercase tracking-[0.18em] text-oth-ink3 font-body mb-1">{label}</div>
        <input
            type="number"
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
            className="w-full h-10 px-3 rounded-full border border-oth-line bg-white text-sm focus:outline-none focus:border-oth-ink"
            data-testid={testId}
        />
        {hint && <div className="mt-1 text-[10px] font-body text-oth-ink3">{hint}</div>}
    </label>
);

const fmt = (v, unit = "", digits = 2) => {
    if (v === null || v === undefined) return "—";
    const n = Number(v);
    if (!Number.isFinite(n)) return "—";
    return unit === "%" ? `${n.toFixed(digits)}%` : n.toFixed(digits);
};

export default function SifControlScreener() {
    const [f, setF] = useState({
        strategy: "",
        min_return_1y: null,
        min_return_3y: null,
        min_sharpe: null,
        max_drawdown: null,
        max_volatility: null,
        min_stars: null,
        min_age_days: null,
    });
    const [data, setData] = useState({ items: [], total: 0 });
    const [loading, setLoading] = useState(false);

    const setField = (k) => (v) => setF({ ...f, [k]: v });

    const run = async () => {
        setLoading(true);
        try {
            const params = Object.fromEntries(Object.entries(f).filter(([_, v]) => v !== null && v !== ""));
            const r = await scScreener(params);
            setData(r);
        } finally { setLoading(false); }
    };
    useEffect(() => { run(); /* eslint-disable-next-line */ }, []);

    const reset = () => setF({ strategy: "", min_return_1y: null, min_return_3y: null, min_sharpe: null, max_drawdown: null, max_volatility: null, min_stars: null, min_age_days: null });

    return (
        <section className="py-14 md:py-20" data-testid="sc-screener-page">
            <div className="mx-auto max-w-[1240px] px-4 md:px-6">
                <div className="text-[11px] uppercase tracking-[0.24em] text-oth-ink3 font-body">Screener</div>
                <h1 className="mt-2 font-display text-4xl md:text-5xl font-semibold tracking-tight text-oth-ink">Find your SIF</h1>
                <p className="mt-4 max-w-[620px] text-oth-ink2 font-body text-base md:text-lg">Filter by strategy, return, risk, cost and rating. Fields left blank are ignored.</p>

                <div className="mt-8 rounded-3xl border border-oth-line bg-white p-6 md:p-8">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <label className="block md:col-span-1">
                            <div className="text-[10px] uppercase tracking-[0.18em] text-oth-ink3 font-body mb-1">Strategy</div>
                            <select value={f.strategy} onChange={(e) => setField("strategy")(e.target.value)} className="w-full h-10 px-3 rounded-full border border-oth-line bg-white text-sm focus:outline-none focus:border-oth-ink" data-testid="sc-screener-strategy">
                                {STRATEGY.map((s) => <option key={s} value={s}>{s || "All strategies"}</option>)}
                            </select>
                        </label>
                        <NumField label="Min 1Y return %" value={f.min_return_1y} onChange={setField("min_return_1y")} testId="sc-screener-r1y" />
                        <NumField label="Min 3Y CAGR %" value={f.min_return_3y} onChange={setField("min_return_3y")} testId="sc-screener-r3y" />
                        <NumField label="Min Sharpe" value={f.min_sharpe} onChange={setField("min_sharpe")} testId="sc-screener-sharpe" />
                        <NumField label="Max drawdown (e.g. -20)" value={f.max_drawdown} onChange={setField("max_drawdown")} hint="Drawdown better than this value" testId="sc-screener-mdd" />
                        <NumField label="Max volatility %" value={f.max_volatility} onChange={setField("max_volatility")} testId="sc-screener-vol" />
                        <NumField label="Min stars" value={f.min_stars} onChange={setField("min_stars")} hint="e.g. 4" testId="sc-screener-stars" />
                        <NumField label="Min fund age (days)" value={f.min_age_days} onChange={setField("min_age_days")} testId="sc-screener-age" />
                    </div>
                    <div className="mt-6 flex items-center gap-3">
                        <button onClick={run} disabled={loading} className="btn-pill btn-pill-dark" data-testid="sc-screener-run">
                            {loading ? <RefreshCw size={13} className="animate-spin" /> : <Search size={13} />} {loading ? "Screening…" : "Show results"}
                        </button>
                        <button onClick={reset} className="btn-pill !text-xs" data-testid="sc-screener-reset">Reset filters</button>
                        {data.ranking && <span className="text-[11px] font-body text-oth-ink3">Rating column uses <b>{data.ranking.name}</b> methodology.</span>}
                    </div>
                </div>

                <div className="mt-8">
                    {loading ? (
                        <div className="p-10 text-center text-sm font-body text-oth-ink3">Loading…</div>
                    ) : data.items.length === 0 ? (
                        <div className="rounded-3xl border border-oth-line bg-white p-10 text-center text-sm font-body text-oth-ink3" data-testid="sc-screener-empty">
                            No SIFs match. Loosen the filters, or wait for more data — as AMFI publishes daily NAVs the eligible universe grows.
                        </div>
                    ) : (
                        <div className="rounded-3xl border border-oth-line bg-white overflow-hidden">
                            <div className="grid grid-cols-[1.4fr_120px_90px_90px_90px_90px_90px] text-[10px] uppercase tracking-[0.16em] text-oth-ink3 font-body px-5 py-3 bg-oth-bg-2/60 border-b border-oth-line">
                                <div>SIF</div><div>AMC</div><div>1Y</div><div>3Y CAGR</div><div>Sharpe</div><div>MDD</div><div className="text-right">Stars</div>
                            </div>
                            <div className="divide-y divide-oth-line" data-testid="sc-screener-list">
                                {data.items.map((r) => (
                                    <Link to={`/sifcontrol/sif/${r.sif.id}`} key={r.sif.id} className="grid grid-cols-[1.4fr_120px_90px_90px_90px_90px_90px] items-center px-5 py-3.5 hover:bg-oth-bg-2 transition-colors" data-testid={`sc-screener-row-${r.sif.id}`}>
                                        <div>
                                            <div className="font-display text-sm font-medium text-oth-ink truncate">{r.sif.sif_name}</div>
                                            <div className="text-[11px] font-body text-oth-ink3">{r.sif.sif_code}</div>
                                        </div>
                                        <div className="text-xs font-body text-oth-ink2 truncate">{r.amc_name || "—"}</div>
                                        <div className="text-xs font-body text-oth-ink">{fmt(r.return_1y, "%")}</div>
                                        <div className="text-xs font-body text-oth-ink">{fmt(r.cagr_3y, "%")}</div>
                                        <div className="text-xs font-body text-oth-ink">{fmt(r.sharpe)}</div>
                                        <div className="text-xs font-body text-oth-ink">{fmt(r.max_drawdown, "%")}</div>
                                        <div className="text-xs font-body text-oth-ink text-right">{r.rating?.star_label || "—"}</div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}
                    <div className="mt-3 text-[11px] font-body text-oth-ink3">{data.total} SIFs match · showing first {data.items.length}</div>
                </div>
            </div>
        </section>
    );
}
